import * as angular from 'angular';

import { 
    TransactionType,
    ComponentDefinition,
    Group,
    ModifiedObjectDto,
    IValueViewModel,
    ConnectionPointViewModel,
    ApplicationUserViewModel,
    ModifiedObjectViewModel,
    Dictionary,
    ValidationPageConfiguration,
    Template,
    IApprovalStrategy,
    State
} from '../../../models';
import { IHistoryService } from '../../../services';
import { dateFormatShort, refreshClick, wideService } from '../../../common';

class ModelHistoryStateController implements angular.IComponentController {
    dateFormat = dateFormatShort;
    isLoadingHistory: boolean;
    lastSync: any;
    loadingMessage: string;
    objects: ModifiedObjectViewModel[];
    selectedItem: ModifiedObjectViewModel;

    readonly approvalStrategiesDictionary: Dictionary<IApprovalStrategy>;
    readonly beginDate: string;
    readonly complexObjectTypes: string[] = ['Group', 'Identifier', 'TagDefinition', 'ConnectionPoint', 'GroupState', 'ReferenceState'];
    readonly connectionPointsDictionary: { [guid: string]: ConnectionPointViewModel };
    readonly endDate: string;
    readonly groupOrReferenceState: string[] = ['GroupState', 'ReferenceState'];
    readonly groupsDictionary: { [guid: string]: Group };
    readonly isAsyncStoreLoaded: boolean;
    readonly isStoreLoaded: boolean;
    readonly statesDictionary: Dictionary<State>;
    readonly templatesDictionary: Dictionary<Template>;
    readonly userViewModels: { [guid: string]: ApplicationUserViewModel };
    readonly validationPageConfigurationsDictionary: Dictionary<ValidationPageConfiguration>;
    readonly viewModelsDictionary: { [guid: string]: IValueViewModel };

    private _garbages: (() => void)[] = [];

    static $inject = [
        '$scope',
        '$translate',
        'historyService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$translate: angular.translate.ITranslateService,
        private _historyService: IHistoryService
    ) {
    }

    $onInit() {
        this._garbages.push(this._$scope.$on(refreshClick, () => {
            this.refresh();
        }));
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        this.refresh();
    }

    isSimpleObjectType(objectType: string): boolean {
        return this.complexObjectTypes.indexOf(objectType) < 0;
    }

    isGroupOrReferenceState(objectType: string): boolean {
        return this.groupOrReferenceState.indexOf(objectType) >= 0;
    }

    private refresh(): void {
        if (this.beginDate && this.endDate && this.isStoreLoaded && this.isAsyncStoreLoaded) {
            this.isLoadingHistory = true;
            this.loadingMessage = 'Loading history';
            this._historyService
                .getLastTransaction(TransactionType.DatabaseSynchronization)
                .then(last => {
                    this.lastSync = last;
                    return this._historyService.getModifiedObject(this.beginDate, this.endDate);
                })
                .then(data => {
                    this.objects = data.map(object => this.mapObject(object));
                })
                .catch(reason => console.error(reason))
                .finally(() => this.isLoadingHistory = false);
        }
    }

    private mapObject(object: ModifiedObjectDto): ModifiedObjectViewModel {
        const vm = Object.assign({}, object as ModifiedObjectViewModel);
        switch (vm.type) {
            case 'Group':
                if (this.groupsDictionary) {
                    if (vm.guid in this.groupsDictionary) {
                        vm.group = this.groupsDictionary[object.guid];
                    }
                }
                break;
            case 'Identifier':
                if (this.viewModelsDictionary) {
                    if (vm.guid in this.viewModelsDictionary) {
                        vm.viewModel = this.viewModelsDictionary[object.guid];
                    }
                }
                break;
            case 'TagDefinition':
                if (this.viewModelsDictionary) {
                    if (vm.guid in this.viewModelsDictionary) {
                        vm.viewModel = this.viewModelsDictionary[object.guid];
                    }
                }
                break;
            case 'ConnectionPoint':
                if (this.connectionPointsDictionary) {
                    if (vm.guid in this.connectionPointsDictionary) {
                        vm.connectionPointViewModel = this.connectionPointsDictionary[object.guid];
                    }
                }
                break;
            case 'GroupState':
                break;
            case 'State':
                if (this.statesDictionary) {
                    if (vm.guid in this.statesDictionary) {
                        vm.state = this.statesDictionary[object.guid];
                    }
                }
                break;
            case 'Template':
                if (this.templatesDictionary) {
                    if (vm.guid in this.templatesDictionary) {
                        vm.template = this.templatesDictionary[object.guid];
                    }
                }
                break;
            case 'IApprovalStrategy':
                if (this.approvalStrategiesDictionary) {
                    if (vm.guid in this.approvalStrategiesDictionary) {
                        vm.approvalStrategie = this.approvalStrategiesDictionary[object.guid];
                    }
                }
                break;
            case 'ValidationPageConfiguration':
                if (this.validationPageConfigurationsDictionary) {
                    if (vm.guid in this.validationPageConfigurationsDictionary) {
                        vm.validationPageConfiguration = this.validationPageConfigurationsDictionary[object.guid];
                    }
                }
                break;
            case 'IdentifierQuery':
                break;
            case 'ReferenceState':
                break;
        }
        if (vm && vm.lastModifier && this.userViewModels) {
            vm.userViewModel = this.getUserViewModel(vm);
        }
        return vm;
    }

    private getUserViewModel(vm: ModifiedObjectViewModel): ApplicationUserViewModel {
        let userViewModel: ApplicationUserViewModel;
        if (vm.lastModifier === wideService) {
            userViewModel = {
                displayName: wideService,
                firstName: wideService,
                initials: 'WS'
            } as ApplicationUserViewModel;
        } else {
            userViewModel = Object.values(this.userViewModels).find(userViewModel => userViewModel.name === vm.lastModifier);
            if (!userViewModel) {
                userViewModel = {
                    displayName: this._$translate.instant('Unknown creator'),
                    initials: ''
                } as ApplicationUserViewModel;
                userViewModel.firstName = userViewModel.displayName;
            }
        }
        return userViewModel;
    }
}

export const wtModelHistoryState: ComponentDefinition = {
    componentId: 'wtModelHistoryState',
    controller: ModelHistoryStateController,
    template: require('./modelHistoryState.component.html'),
    controllerAs: 'vm',
    bindings: {
        approvalStrategiesDictionary: '<',
        beginDate: '<',
        connectionPointsDictionary: '<',
        endDate: '<',
        groupsDictionary: '<',
        isAsyncStoreLoaded: '<',
        isStoreLoaded: '<',
        statesDictionary: '<',
        templatesDictionary: '<',
        userViewModels: '<',
        validationPageConfigurationsDictionary: '<',
        viewModelsDictionary: '<'
    }
};
